import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { SuperAdminDashboardHeader } from "./DashboardHeader";
import { DataTable, DateField } from "@root/components";
import { TableColumns } from "./TableColumns";
import { DELETE, EDIT } from "@root/constants";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CustomPopup } from "@root/components/common/modals/CustomPopup";
import SearchInput from "@root/components/common/SearchInput";
import { CustomButton } from "@root/components/common/button/customButton";
import { AddIcon } from "@chakra-ui/icons";
import { AddAdmin } from "@root/components/SuperAdminPages/AddAdmin";
import { SuperAdminPlaceholderIcon } from "@root/assets/icons/SuperAdminPlaceholderIcon";
import useAdminHook from "@root/hooks/useAdminHook";
import { ModalDelete } from "@root/components/modal-delete";
import CommonDateField from "@root/components/date-field/CommonDateField";
import SectionHeader from "@root/components/kpec-admin/subscriber-management/sectionheader";

export const SuperAdminDashboard = () => {
  const {
    organizations,
    fetchOrganizations,
    createAdmin,
    updateAdmin,
    deleteAdmin,
    search,
    setSearch,
    selectedDate,
    setSelectedDate,
    page,
    setPage,
    pageCount,
    loader,
  } = useAdminHook();
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [isAddJournalistOpen, setIsAddJournalistOpen] = useState(false);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const { t } = useTranslation("common");
  const isInitialMount = useRef(true);

  // Define handleActions before using it in TableColumns
  function handleActions(type: string, rowData: any) {
    if (type === EDIT) {
      setSelectedRow(rowData);
      handleOpenAddJournalist();
    } else if (type === DELETE) {
      setSelectedRow(rowData); // Store the row to be deleted
      setIsDeleteModal(true);
    }
  }

  // Always call TableColumns at the top level to avoid hook order issues
  const columns = TableColumns(handleActions);

  function handlePageChange(newPage: number) {
    setPage(newPage);
  }

  function handleOpenAddJournalist() {
    setIsAddJournalistOpen(true);
  }

  function handleCloseAddJournalist() {
    setIsAddJournalistOpen(false);
    setSelectedRow(null);
  }

  const addNewAdmin = async (values: any) => {
    const formData = new FormData();
    formData.append("email", values?.email || "");
    formData.append("name", values?.name || "");
    if (values?.certificateOrgName) formData.append("certificateOrgName", values.certificateOrgName);
    if (values?.certificateOrgNameKr) formData.append("certificateOrgNameKr", values.certificateOrgNameKr);
    if (values?.domain) formData.append("domain", values.domain);
    if (values?.browserTabName) formData.append("browserTabName", values.browserTabName);
    if (values?.chairmanNameKr) formData.append("chairmanNameKr", values.chairmanNameKr);
    if (values?.chairmanNameEn) formData.append("chairmanNameEn", values.chairmanNameEn);

    if (values?.logo instanceof File) {
      formData.append("logo", values.logo);
    }
    if (values?.favicon instanceof File) {
      formData.append("favicon", values.favicon);
    }
    if (values?.certificateBorderImage instanceof File) {
      formData.append("certificateBorderImage", values.certificateBorderImage);
    }
    if (values?.stamp instanceof File) {
      formData.append("stamp", values.stamp);
    }

    await createAdmin(formData);
    handleCloseAddJournalist();
  };

  const editAdmin = async (values: any) => {
    const formData = new FormData();
    // For update, we might not need to send email if it's read-only, but keeping it if needed
    formData.append("id", values?.id);
    formData.append("name", values?.name || "");
    if (values?.certificateOrgName) formData.append("certificateOrgName", values.certificateOrgName);
    if (values?.certificateOrgNameKr) formData.append("certificateOrgNameKr", values.certificateOrgNameKr);
    if (values?.domain) formData.append("domain", values.domain);
    if (values?.browserTabName) formData.append("browserTabName", values.browserTabName);
    if (values?.chairmanNameKr) formData.append("chairmanNameKr", values.chairmanNameKr);
    if (values?.chairmanNameEn) formData.append("chairmanNameEn", values.chairmanNameEn);

    if (values?.logo instanceof File) {
      formData.append("logo", values.logo);
    }
    if (values?.favicon instanceof File) {
      formData.append("favicon", values.favicon);
    }
    if (values?.certificateBorderImage instanceof File) {
      formData.append("certificateBorderImage", values.certificateBorderImage);
    }
    if (values?.stamp instanceof File) {
      formData.append("stamp", values.stamp);
    }

    await updateAdmin(formData);
    handleCloseAddJournalist();
  };

  const removeAdmin = async (values: any) => {
    await deleteAdmin(values);
  };

  // Fetch data when page changes
  useEffect(() => {
    fetchOrganizations({}, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Reset page to 1 and fetch data when filters change
  useEffect(() => {
    // Skip on initial mount to avoid resetting page unnecessarily
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setPage(1);
    // Fetch with page 1 when search or date changes
    fetchOrganizations({}, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, search]);

  const renderHeader = () => {
    return (
      <Box>
        <SectionHeader />
      </Box>
    );
  };

  const renderDataTable = () => {
    // Ensure pageCount is a valid number, and if we have exactly pageSize rows,
    // assume there might be more pages (fallback if API doesn't return count)
    const rowsLength = organizations?.rows?.length || 0;
    const effectivePageCount = pageCount > 0
      ? pageCount
      : rowsLength === 10
        ? 11 // Assume there might be more pages if we have exactly 10 rows
        : rowsLength;

    return (
      <Box>
        <DataTable
          columns={columns}
          data={organizations?.rows}
          showPagination={true}
          page={page}
          pageCount={effectivePageCount}
          pageSize={10}
          theadBg="#f3f1ff"
          handlePageChange={handlePageChange}
        />
      </Box>
    );
  };
  return (
    <Flex
      minW={"100%"}
      position={"relative"}
      flexDir={"column"}
      className="projectsDashboard"
      gap={"16px"}
      p={"16px"}
    >
      {renderHeader()}
      <Box>
        <Text fontSize={"24px"} fontWeight={600} color={"#2F2F34"}>
          {t("superAdminDashboard.managePlatformUsers")}
        </Text>
        <Text mt={"8px"} fontSize={"16px"} fontWeight={400} color={"#798692"}>
          {t("superAdminDashboard.manageAndAddAdmins")}
        </Text>
      </Box>
      <Flex justifyContent={"space-between"} mt={"16px"} gap={"16px"}>
        <Flex gap={"16px"}>
          <SearchInput
            placeholder={t("superAdminDashboard.searchPlaceholder")}
            width={"450px"}
            value={search}
            onChange={(e: any) => {
              setSearch(e.target.value);
            }}
          />
          <CommonDateField
            borderColor={"#CDD6DC"}
            color="#5C6670"
            borderRadius="12px"
            height="44px"
            placeholder={t("superAdminDashboard.selectDate")}
            selectedDate={selectedDate ? new Date(selectedDate) : null}
            onChange={(date: any) => {
              setSelectedDate(date);
            }}
          />
        </Flex>
        <CustomButton
          leftIcon={<AddIcon color="#fff" />}
          text={t("superAdminDashboard.addNewAdmin")}
          colorScheme="purple"
          borderRadius="8px"
          width="fit-content"
          fontSize="14px"
          fontWeight={500}
          height="48px"
          onClick={handleOpenAddJournalist}
          background="#1819B4"
          _hover={{ background: "#1819B4" }}
        />
      </Flex>
      <Box maxH={"calc(100vh - 180px)"} overflowY={"auto"}>
        {loader ? (
          <Flex justifyContent="center" alignItems="center" minH="200px">
            <Spinner size="xl" color="#1819B4" />
          </Flex>
        ) : !organizations?.rows || organizations?.rows?.length === 0 ? (
          <Flex
            flexDir={"column"}
            alignItems="center"
            justifyContent="center"
            flex={1}
            minH="400px"
          >
            <SuperAdminPlaceholderIcon />
            <Text fontSize={"20px"} fontWeight={600} color={"#000000"}>
              {search || selectedDate
                ? t("superAdminDashboard.noResultsFound") || "No results found"
                : t("superAdminDashboard.noAdminYet")}
            </Text>
            <Text
              mb={"16px"}
              mt={"8px"}
              fontSize={"16px"}
              fontWeight={400}
              color={"#000000"}
            >
              {search || selectedDate
                ? t("superAdminDashboard.tryDifferentSearch") ||
                "Try adjusting your search or filters"
                : t("superAdminDashboard.addAdminHint")}
            </Text>
          </Flex>
        ) : (
          renderDataTable()
        )}
      </Box>
      <CustomPopup
        width={"750px"}
        borderRadius={"20px"}
        p={"5px"}
        open={isAddJournalistOpen}
        title={
          selectedRow
            ? t("superAdminDashboard.updateAnAdmin")
            : t("superAdminDashboard.addAnAdmin")
        }
        onClose={handleCloseAddJournalist}
      >
        <AddAdmin
          selectedRow={selectedRow}
          onCancel={handleCloseAddJournalist}
          onSubmit={(values: { name: string; email: string }) =>
            selectedRow ? editAdmin(values) : addNewAdmin(values)
          }
        />
      </CustomPopup>

      <ModalDelete
        title={t("superAdminDashboard.removeTitle")}
        description={t("superAdminDashboard.removeConfirm")}
        deleteButtonTitle={t("superAdminDashboard.delete")}
        cancelButtonTitle={t("superAdminDashboard.cancel")}
        isOpen={isDeleteModal}
        onClose={() => {
          setIsDeleteModal(false);
        }}
        onConfirm={() => {
          if (selectedRow && selectedRow.id) {
            removeAdmin({ id: selectedRow.id });
          }
          setIsDeleteModal(false);
        }}
      />
    </Flex>
  );
};
